/**
 * Iconos Lucide-style con stroke 1.6 (portado de Traceability 2).
 * Componentes simples sin dependencia externa.
 */
import type { SVGProps } from "react";

interface Props extends Omit<SVGProps<SVGSVGElement>, "viewBox"> {
  size?: number;
  sw?: number;
}

function Icon({ size = 16, sw = 1.6, children, ...rest }: Props & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  );
}

export const Icons = {
  Activity: (p: Props) => (
    <Icon {...p}><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></Icon>
  ),
  Cube: (p: Props) => (
    <Icon {...p}>
      <path d="M21 16V8l-9-5-9 5v8l9 5 9-5z" />
      <path d="M3.3 7L12 12l8.7-5M12 22V12" />
    </Icon>
  ),
  Flag: (p: Props) => <Icon {...p}><path d="M4 21V4M4 4h14l-3 5 3 5H4" /></Icon>,
  Users: (p: Props) => (
    <Icon {...p}>
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
    </Icon>
  ),
  Bookmark: (p: Props) => <Icon {...p}><path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" /></Icon>,
  Cog: (p: Props) => (
    <Icon {...p}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </Icon>
  ),
  Search: (p: Props) => <Icon {...p}><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></Icon>,
  Plus: (p: Props) => <Icon {...p} sw={2}><path d="M12 5v14M5 12h14" /></Icon>,
  Bell: (p: Props) => <Icon {...p}><path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 01-3.4 0" /></Icon>,
  ChevDown: (p: Props) => <Icon {...p}><path d="M6 9l6 6 6-6" /></Icon>,
  Filter: (p: Props) => <Icon {...p}><path d="M3 6h18M6 12h12M10 18h4" /></Icon>,
  Zap: (p: Props) => <Icon {...p}><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" /></Icon>,
  Shield: (p: Props) => <Icon {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></Icon>,
  Mail: (p: Props) => <Icon {...p}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></Icon>,
  Brain: (p: Props) => (
    <Icon {...p}>
      <path d="M12 4a3 3 0 016 0v2a4 4 0 010 8v6a4 4 0 01-8 0M12 4a3 3 0 00-6 0v2a4 4 0 000 8" />
    </Icon>
  ),
  Logout: (p: Props) => (
    <Icon {...p}>
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
    </Icon>
  ),
  Sun: (p: Props) => (
    <Icon {...p}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </Icon>
  ),
  Moon: (p: Props) => <Icon {...p}><path d="M21 12.8A9 9 0 1111.2 3a7 7 0 009.8 9.8z" /></Icon>,
  External: (p: Props) => <Icon {...p}><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" /></Icon>,
  Close: (p: Props) => <Icon {...p}><path d="M18 6L6 18M6 6l12 12" /></Icon>,
  ChevronDown: (p: Props) => <Icon {...p}><path d="M6 9l6 6 6-6" /></Icon>,
  ChevronUp: (p: Props) => <Icon {...p}><path d="M18 15l-6-6-6 6" /></Icon>,
  Trash: (p: Props) => <Icon {...p}><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" /></Icon>,
  Pencil: (p: Props) => <Icon {...p}><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></Icon>,
  Menu: (p: Props) => <Icon {...p}><path d="M3 6h18M3 12h18M3 18h18" /></Icon>,
  Book: (p: Props) => <Icon {...p}><path d="M4 19.5A2.5 2.5 0 016.5 17H20V3H6.5A2.5 2.5 0 004 5.5v14z" /><path d="M4 19.5A2.5 2.5 0 016.5 22H20v-5H6.5A2.5 2.5 0 004 19.5z" /></Icon>,
  Copy: (p: Props) => <Icon {...p}><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></Icon>,
  Check: (p: Props) => <Icon {...p}><path d="M20 6L9 17l-5-5" /></Icon>,
  Clock: (p: Props) => <Icon {...p}><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></Icon>,
  TrendUp: (p: Props) => <Icon {...p}><path d="M3 17l6-6 4 4 8-8M14 7h7v7" /></Icon>,
  AlertTriangle: (p: Props) => (
    <Icon {...p}><path d="M12 2L1 21h22L12 2z" /><path d="M12 9v5M12 18v.01" /></Icon>
  ),
  PanelLeft: (p: Props) => (
    <Icon {...p}><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 3v18" /></Icon>
  ),
  Info: (p: Props) => (
    <Icon {...p}><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></Icon>
  ),
  Send: (p: Props) => <Icon {...p}><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" /></Icon>,
  FileText: (p: Props) => (
    <Icon {...p}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" /></Icon>
  ),
  Hash: (p: Props) => <Icon {...p}><path d="M4 9h16M4 15h16M10 3L8 21M16 3l-2 18" /></Icon>,
  Sparkles: (p: Props) => (
    <Icon {...p}><path d="M12 3v6m0 6v6m-9-9h6m6 0h6M5.6 5.6l4.2 4.2m4.4 4.4l4.2 4.2M18.4 5.6l-4.2 4.2m-4.4 4.4l-4.2 4.2" /></Icon>
  ),
  Database: (p: Props) => (
    <Icon {...p}><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M3 5v6c0 1.7 4 3 9 3s9-1.3 9-3V5M3 11v6c0 1.7 4 3 9 3s9-1.3 9-3v-6" /></Icon>
  ),
};
